import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { useMap } from '../../context/MapContext';
import { useAuth } from '../../context/AuthContext';
import { catalogAPI } from '../../services/api';
import LayerCard from './LayerCard';
import LayerCustomize from './LayerCustomize';
import './Sidebar.css';

const GEOSERVER_BASE =
  process.env.REACT_APP_GEOSERVER_URL || `${window.location.origin}/geoserver`;

function buildWmsLayer(catalogLayer, municipalityCode) {
  let baseUrl = GEOSERVER_BASE + `/${catalogLayer.workspace}/wms`;
  let layersParam = `${catalogLayer.workspace}:${catalogLayer.layerId}`;
  if (catalogLayer.wmsUrl) {
    try {
      const urlObj = new URL(catalogLayer.wmsUrl);
      const geoPath = urlObj.pathname.replace(/^.*\/geoserver/, '');
      baseUrl = GEOSERVER_BASE + geoPath;
      const lp = urlObj.searchParams.get('layers') || urlObj.searchParams.get('LAYERS');
      if (lp) layersParam = lp;
    } catch { }
  }
  return {
    name: catalogLayer.title || catalogLayer.layerName,
    baseUrl,
    layers: layersParam,
    format: 'image/png',
    transparent: true,
    version: '1.1.1',
    cqlFilter: municipalityCode ? `mun_ref='${municipalityCode}'` : null,
  };
}

function normalize(str) {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function filterFolderTree(folders, allowedLayerIds) {
  return (folders || [])
    .map((folder) => {
      const layers = (folder.layers || []).filter((layer) => allowedLayerIds.has(layer.layerId));
      const nestedFolders = filterFolderTree(folder.folders || [], allowedLayerIds);

      if (!layers.length && !nestedFolders.length) {
        return null;
      }

      return {
        ...folder,
        layers,
        folders: nestedFolders,
      };
    })
    .filter(Boolean);
}

function filterTreeByLayerIds(tree, allowedLayerIds) {
  return (tree || [])
    .map((group) => {
      const categories = (group.categories || [])
        .map((category) => {
          const layers = (category.layers || []).filter((layer) => allowedLayerIds.has(layer.layerId));
          const folders = filterFolderTree(category.folders || [], allowedLayerIds);

          if (!layers.length && !folders.length) {
            return null;
          }

          return {
            ...category,
            layers,
            folders,
          };
        })
        .filter(Boolean);

      if (!categories.length) {
        return null;
      }

      return {
        ...group,
        categories,
      };
    })
    .filter(Boolean);
}

/* ── Ícone de grupo (imagem PNG ou emoji legado) ── */
function GroupIcon({ icon, size = 36 }) {
  if (!icon) return <span style={{ fontSize: 22 }} aria-hidden="true">📁</span>;
  if (icon.startsWith('/icones/')) {
    return (
      <img
        src={icon}
        alt=""
        aria-hidden="true"
        style={{ width: size, height: size, objectFit: 'contain', display: 'block' }}
      />
    );
  }
  return <span aria-hidden="true">{icon}</span>;
}

/* ── Ícone de pasta ── */
function FolderIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  );
}

/* ── Toggle switch para camada ── */
function LayerToggleCard({ layer, isOn, onToggle }) {
  const layerTitle = layer.title || layer.layerName;

  return (
    <div className={`sb-layer-card${isOn ? ' sb-layer-card--on' : ''}`}>
      <div className="sb-layer-card-info">
        <span className="sb-layer-card-name" title={layerTitle}>{layerTitle}</span>
        {layer.description && (
          <span className="sb-layer-card-source" title={layer.description}>{layer.description}</span>
        )}
      </div>
      <button
        className={`sb-toggle${isOn ? ' sb-toggle--on' : ''}`}
        onClick={() => onToggle(layer)}
        role="switch"
        aria-checked={isOn}
        aria-label={isOn ? `Desativar ${layerTitle}` : `Ativar ${layerTitle}`}
      >
        <span className="sb-toggle-thumb" aria-hidden="true" />
      </button>
    </div>
  );
}

/* ── Conta camadas recursivamente (pastas + diretas) ── */
function countAllLayers(node) {
  return (node.layers || []).length +
    (node.folders || []).reduce((s, f) => s + countAllLayers(f), 0);
}

/* ── Achata todas as camadas de pastas numa lista plana ── */
function flattenLayers(node, catName, groupName, folderPath) {
  const direct = (node.layers || []).map((l) => ({ layer: l, catName, groupName, folderPath }));
  const nested = (node.folders || []).flatMap((f) =>
    flattenLayers(f, catName, groupName, folderPath ? `${folderPath} › ${f.name}` : f.name)
  );
  return [...direct, ...nested];
}

/* ── Accordion de pasta (recursivo) ── */
function FolderAccordion({ folder, activeIds, onToggle, depth }) {
  const [open, setOpen] = useState(false);
  const total = countAllLayers(folder);

  return (
    <div className="sb-folder" style={{ paddingLeft: depth * 10 }}>
      <button
        className={`sb-folder-header${open ? ' sb-folder-header--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="sb-accordion-chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span className="sb-accordion-folder"><FolderIcon /></span>
        <span className="sb-accordion-name">{folder.name}</span>
        <span className="sb-accordion-count">{total}</span>
      </button>
      {open && (
        <div className="sb-folder-body">
          {(folder.layers || []).map((layer) => (
            <LayerToggleCard key={layer.id} layer={layer}
              isOn={activeIds.has(layer.layerId)} onToggle={onToggle} />
          ))}
          {(folder.folders || []).map((sub) => (
            <FolderAccordion key={sub.id} folder={sub}
              activeIds={activeIds} onToggle={onToggle} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Accordion de subcategoria ── */
function SubcategoryAccordion({ category, activeIds, onToggle, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen || false);
  const total = countAllLayers(category);

  return (
    <div className="sb-accordion">
      <button
        className={`sb-accordion-header${open ? ' sb-accordion-header--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="sb-accordion-chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span className="sb-accordion-folder"><FolderIcon /></span>
        <span className="sb-accordion-name">{category.name}</span>
        <span className="sb-accordion-count">{total}</span>
      </button>

      {open && (
        <div className="sb-accordion-body">
          {total === 0 && (
            <p className="sb-accordion-empty">Nenhuma camada nesta categoria.</p>
          )}
          {/* Camadas diretamente na categoria */}
          {(category.layers || []).map((layer) => (
            <LayerToggleCard key={layer.id} layer={layer}
              isOn={activeIds.has(layer.layerId)} onToggle={onToggle} />
          ))}
          {/* Pastas (recursivo) */}
          {(category.folders || []).map((folder) => (
            <FolderAccordion key={folder.id} folder={folder}
              activeIds={activeIds} onToggle={onToggle} depth={0} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Resultados de pesquisa ── */
function SearchResults({ tree, query, activeIds, onToggle }) {
  const normalizedQ = normalize(query);
  const results = [];
  for (const group of tree) {
    for (const cat of group.categories) {
      const allLayers = flattenLayers(cat, cat.name, group.name, '');
      for (const { layer, catName, groupName, folderPath } of allLayers) {
        if (
          normalize(layer.layerName).includes(normalizedQ) ||
          normalize(catName).includes(normalizedQ) ||
          normalize(groupName).includes(normalizedQ) ||
          (folderPath && normalize(folderPath).includes(normalizedQ))
        ) {
          results.push({ layer, catName, groupName, folderPath });
        }
      }
    }
  }

  if (results.length === 0) {
    return (
      <div className="sb-search-empty">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <p>Nenhuma camada encontrada para <strong>"{query}"</strong></p>
      </div>
    );
  }

  return (
    <div className="sb-search-results">
      {results.map(({ layer, catName, groupName, folderPath }) => (
        <div key={layer.id} className="sb-search-item">
          <p className="sb-search-path">
            {groupName} › {catName}
            {folderPath && <span className="sb-search-folder"> › {folderPath}</span>}
          </p>
          <LayerToggleCard
            layer={layer}
            isOn={activeIds.has(layer.layerId)}
            onToggle={onToggle}
          />
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════
   SIDEBAR PRINCIPAL
   ══════════════════════════════════ */
function Sidebar() {
  const { hasRole } = useAuth();
  const {
    wmsLayers,
    sidebarCollapsed,
    toggleSidebar,
    reorderWmsLayers,
    addWmsLayer,
    removeWmsLayer,
    selectedMunicipality,
    selectedPeriod,
  } = useMap();
  const isSituacional = !selectedPeriod || selectedPeriod.id === 'situacional';
  const canViewAllLayers = hasRole(['moderador', 'visualizador_interno']);

  /* Catálogo */
  const [tree,    setTree]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableLayerIds, setAvailableLayerIds] = useState(null);

  /* Navegação */
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [panelOpen,     setPanelOpen]     = useState(false);

  /* Pesquisa */
  const [search,          setSearch]          = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchRef = useRef(null);
  const [hoveredGroupLabel, setHoveredGroupLabel] = useState(null);

  /* Camadas ativas */
  const [activesOpen,  setActivesOpen]  = useState(true);
  const [customizeId,  setCustomizeId]  = useState(null);
  const [activeId,     setActiveId]     = useState(null);

  /* Carrega árvore */
  useEffect(() => {
    let cancelled = false;
    catalogAPI.getTree()
      .then((res) => {
        if (cancelled) return;
        setTree(res.data);
        if (res.data.length > 0 && !activeGroupId) {
          setActiveGroupId(res.data[0].id);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (canViewAllLayers) {
      setAvailableLayerIds(null);
      return;
    }

    if (!selectedMunicipality?.code) {
      setAvailableLayerIds(null);
      return;
    }

    let cancelled = false;

    catalogAPI.getAvailableLayers(selectedMunicipality.code)
      .then((res) => {
        if (cancelled) return;
        setAvailableLayerIds(new Set(res.data.layerIds || []));
      })
      .catch(() => {
        if (!cancelled) {
          setAvailableLayerIds(new Set());
        }
      });

    return () => { cancelled = true; };
  }, [canViewAllLayers, selectedMunicipality?.code]);

  /* Debounce pesquisa */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  /* Fecha customize se a camada for removida */
  useEffect(() => {
    if (customizeId && !wmsLayers.find((l) => l.id === customizeId)) {
      setCustomizeId(null);
    }
  }, [wmsLayers, customizeId]);

  /* DnD */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const sortedLayers = wmsLayers.slice().sort((a, b) => a.order - b.order);
  const handleDragStart  = useCallback((e) => setActiveId(e.active.id), []);
  const handleDragCancel = useCallback(() => setActiveId(null), []);
  const handleDragEnd    = useCallback((e) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = wmsLayers.slice().sort((a, b) => a.order - b.order).map((l) => l.id);
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    reorderWmsLayers(arrayMove(ids, oldIdx, newIdx));
  }, [wmsLayers, reorderWmsLayers]);

  /* Toggle de camada (add/remove do mapa) */
  const activeIds = new Set(
    wmsLayers.map((l) => { const p = l.layers.split(':'); return p.length > 1 ? p[1] : l.layers; })
  );
  const handleToggle = useCallback((catalogLayer) => {
    const found = wmsLayers.find((l) => {
      const p = l.layers.split(':');
      return (p.length > 1 ? p[1] : l.layers) === catalogLayer.layerId;
    });
    if (found) {
      removeWmsLayer(found.id);
      return;
    }

    addWmsLayer(buildWmsLayer(catalogLayer, selectedMunicipality?.code));
  }, [
    wmsLayers,
    addWmsLayer,
    removeWmsLayer,
    selectedMunicipality,
  ]);

  /* Clique no ícone de grupo */
  function handleIconClick(groupId) {
    if (groupId === activeGroupId) {
      setPanelOpen((v) => !v);
    } else {
      setActiveGroupId(groupId);
      setPanelOpen(true);
      setSearch('');
    }
  }

  function handleGroupHover(event, label) {
    const buttonRect = event.currentTarget.getBoundingClientRect();
    setHoveredGroupLabel({
      text: label,
      top: buttonRect.top + (buttonRect.height / 2),
      left: buttonRect.right + 12,
    });
  }

  function clearGroupHover() {
    setHoveredGroupLabel(null);
  }

  const visibleTree = !canViewAllLayers && selectedMunicipality?.code && availableLayerIds
    ? filterTreeByLayerIds(tree, availableLayerIds)
    : tree;

  useEffect(() => {
    if (!tree.length) {
      if (activeGroupId !== null) {
        setActiveGroupId(null);
      }
      setPanelOpen(false);
      return;
    }

    if (!tree.some((group) => group.id === activeGroupId)) {
      setActiveGroupId(tree[0].id);
    }
  }, [tree, activeGroupId]);

  const activeGroup = tree.find((g) => g.id === activeGroupId);
  const activeVisibleGroup = visibleTree.find((g) => g.id === activeGroupId);
  const customizeLayer = customizeId ? wmsLayers.find((l) => l.id === customizeId) : null;
  const activeLayer    = activeId    ? wmsLayers.find((l) => l.id === activeId)    : null;

  return (
    <aside
      className={[
        'sidebar',
        sidebarCollapsed            ? 'sidebar--collapsed'   : '',
        !sidebarCollapsed && panelOpen && activeGroup ? 'sidebar--full' : '',
      ].filter(Boolean).join(' ')}
    >
      {/* ════════════════════════════════
          COLUNA 1 — Barra de ícones
          ════════════════════════════════ */}
      {!sidebarCollapsed && (
        <nav className="sb-iconbar" aria-label="Categorias de camadas">
          {loading ? (
            <div className="sb-iconbar-loading" aria-hidden="true">
              <span className="sb-iconbar-spinner" />
            </div>
          ) : !isSituacional ? null : (
            tree.map((group) => {
              const isActive = group.id === activeGroupId && panelOpen;
              return (
                <button
                  key={group.id}
                  className={`sb-icon-btn${isActive ? ' sb-icon-btn--active' : ''}`}
                  onClick={() => handleIconClick(group.id)}
                  onMouseEnter={(event) => handleGroupHover(event, group.name)}
                  onMouseLeave={clearGroupHover}
                  onFocus={(event) => handleGroupHover(event, group.name)}
                  onBlur={clearGroupHover}
                  aria-label={group.name}
                  aria-pressed={isActive}
                >
                  <GroupIcon icon={group.icon} size={36} />
                </button>
              );
            })
          )}

          {/* Recolher barra de ícones */}
          <button
            className="sb-iconbar-collapse"
            onClick={toggleSidebar}
            title="Recolher painel"
            aria-label="Recolher painel"
          >
            ‹
          </button>
        </nav>
      )}

      {/* Botão para reexpandir quando totalmente recolhido */}
      {sidebarCollapsed && (
        <button
          className="sb-expand-btn"
          onClick={toggleSidebar}
          title="Expandir painel"
          aria-label="Expandir painel"
        >
          ›
        </button>
      )}

      {/* ════════════════════════════════
          COLUNA 2 — Painel de conteúdo
          ════════════════════════════════ */}
      {!sidebarCollapsed && panelOpen && activeGroup && (
        customizeLayer ? (
          <LayerCustomize
            layer={customizeLayer}
            onClose={() => setCustomizeId(null)}
          />
        ) : (
          <div className="sb-panel">

            {/* Cabeçalho */}
            <div className="sb-panel-header">
              <span className="sb-panel-title">{activeGroup.name}</span>
              <button
                className="sb-panel-close"
                onClick={() => setPanelOpen(false)}
                aria-label="Fechar painel"
                title="Fechar painel"
              >
                ‹
              </button>
            </div>

            {/* Pesquisa */}
            <div className="sb-search">
              <svg className="sb-search-icon" width="14" height="14" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                ref={searchRef}
                className="sb-search-input"
                type="search"
                placeholder="Pesquisar camadas…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Pesquisar camadas"
              />
              {search && (
                <button
                  className="sb-search-clear"
                  onClick={() => { setSearch(''); searchRef.current?.focus(); }}
                  aria-label="Limpar pesquisa"
                >×</button>
              )}
            </div>

            {/* Corpo scrollável */}
            <div className="sb-panel-body">
              {!isSituacional ? (
                <p className="sb-panel-empty">
                  As camadas deste horizonte ainda não foram cadastradas.
                </p>
              ) : !selectedMunicipality ? (
                <p className="sb-panel-empty">
                  Selecione um município para visualizar as camadas disponíveis.
                </p>
              ) : !canViewAllLayers && availableLayerIds && !activeVisibleGroup ? (
                <p className="sb-panel-empty">
                  Não há shapes com correspondência para este município.
                </p>
              ) : debouncedSearch ? (
                <SearchResults
                  tree={visibleTree}
                  query={debouncedSearch}
                  activeIds={activeIds}
                  onToggle={handleToggle}
                />
              ) : (
                <>
                  {activeVisibleGroup?.categories.length === 0 && (
                    <p className="sb-panel-empty">Nenhuma subcategoria neste grupo.</p>
                  )}
                  {activeVisibleGroup?.categories.map((cat, idx) => (
                    <SubcategoryAccordion
                      key={cat.id}
                      category={cat}
                      activeIds={activeIds}
                      onToggle={handleToggle}
                      defaultOpen={idx === 0}
                    />
                  ))}
                </>
              )}
            </div>

            {/* Camadas ativas */}
            <div className="sb-actives">
              <button
                className="sb-actives-header"
                onClick={() => setActivesOpen((v) => !v)}
                aria-expanded={activesOpen}
                aria-controls="sb-actives-body"
              >
                <svg
                  className={`sb-actives-chevron${activesOpen ? ' sb-actives-chevron--open' : ''}`}
                  width="11" height="11" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
                <span className="sb-actives-title">MAPAS ATIVOS</span>
                <span className="sb-actives-badge">{wmsLayers.length}</span>
              </button>

              <div
                id="sb-actives-body"
                className={`sb-actives-body${activesOpen ? ' sb-actives-body--open' : ''}`}
              >
                {wmsLayers.length === 0 ? (
                  <p className="sb-actives-empty">Nenhuma camada ativa.</p>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                  >
                    <SortableContext
                      items={sortedLayers.map((l) => l.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {sortedLayers.map((layer) => (
                        <LayerCard
                          key={layer.id}
                          layer={layer}
                          onCustomize={() => setCustomizeId(layer.id)}
                        />
                      ))}
                    </SortableContext>
                    <DragOverlay dropAnimation={{ duration: 150, easing: 'ease' }}>
                      {activeLayer ? (
                        <div className="layer-card-drag-overlay">
                          <span className="drag-overlay-handle">⠿</span>
                          <span className="drag-overlay-name">{activeLayer.name}</span>
                        </div>
                      ) : null}
                    </DragOverlay>
                  </DndContext>
                )}
              </div>
            </div>

          </div>
        )
      )}

      {hoveredGroupLabel && !sidebarCollapsed && ReactDOM.createPortal(
        <div
          className="sb-icon-tooltip"
          style={{
            top: hoveredGroupLabel.top,
            left: hoveredGroupLabel.left,
          }}
          aria-hidden="true"
        >
          {hoveredGroupLabel.text}
        </div>,
        document.body
      )}
    </aside>
  );
}

export default Sidebar;
